import { Component, Input, OnChanges, SimpleChanges, inject } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { MatTableModule } from '@angular/material/table';
import { MatIconModule } from '@angular/material/icon';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatChipsModule } from '@angular/material/chips';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map, take } from 'rxjs/operators';

import { ContractService } from '../../service/entity-services/contract.service';
import { BaaService } from '../../service/entity-services/baa.service';
import { CoiService } from '../../service/entity-services/coi.service';

export interface VersionRow {
  versionNumber: number;
  name: string;
  status: string;
  supersededDate: string;
  entityNum: string;
  entityType: string;
}

@Component({
  selector: 'app-versions',
  templateUrl: './versions.component.html',
  styleUrls: ['./versions.component.css'],
  standalone: true,
  imports: [
    CommonModule,
    MatTableModule,
    MatIconModule,
    MatCardModule,
    MatButtonModule,
    MatChipsModule,
    MatProgressSpinnerModule,
    DatePipe
  ]
})
export class VersionsComponent implements OnChanges {
  private contractService = inject(ContractService);
  private baaService = inject(BaaService);
  private coiService = inject(CoiService);

  @Input() entityType: 'contract' | 'baa' | 'coi' = 'contract';
  @Input() versionList: string[] = [];
  @Input() currentEntityNum = '';

  displayedColumns = ['versionNumber', 'name', 'status', 'supersededDate', 'actions'];
  versions: VersionRow[] = [];
  loading = false;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['versionList'] || changes['currentEntityNum']) {
      this.loadVersions();
    }
  }

  private loadVersions(): void {
    if (!this.versionList || this.versionList.length === 0) {
      this.versions = [];
      return;
    }

    // Filter out current entity from the version list
    const previousVersionNums = this.versionList.filter(
      num => num !== this.currentEntityNum
    );

    if (previousVersionNums.length === 0) {
      this.versions = [];
      return;
    }

    this.loading = true;

    const fetches$: Observable<VersionRow | null>[] = previousVersionNums.map(
      (entityNum, index) => this.fetchEntity(entityNum, index + 1)
    );

    forkJoin(fetches$).subscribe({
      next: (results) => {
        this.versions = results
          .filter((v): v is VersionRow => v !== null)
          .sort((a, b) => b.versionNumber - a.versionNumber);
        this.loading = false;
      },
      error: () => {
        this.versions = [];
        this.loading = false;
      }
    });
  }

  private fetchEntity(entityNum: string, versionNumber: number): Observable<VersionRow | null> {
    switch (this.entityType) {
      case 'contract':
        return this.contractService.getContractByNumber(entityNum).pipe(
          take(1),
          map(contract => contract ? {
            versionNumber: contract.version || versionNumber,
            name: contract.nameOfContract || (contract as any).friendlyName || 'Untitled',
            status: contract.status || 'Superseded',
            supersededDate: (contract as any).updatedAt || (contract as any).createdAt || '',
            entityNum: contract.contractNum || entityNum,
            entityType: 'contract'
          } : null),
          catchError(() => of(null))
        );

      case 'baa':
        return this.baaService.getBaaByNumber(entityNum).pipe(
          take(1),
          map(baa => baa ? {
            versionNumber,
            name: baa.coveredEntity || baa.friendlyName || 'Untitled BAA',
            status: baa.status || 'Superseded',
            supersededDate: baa.updatedAt || baa.createdAt || '',
            entityNum: baa.baaNum || entityNum,
            entityType: 'baa'
          } : null),
          catchError(() => of(null))
        );

      case 'coi':
        return this.coiService.getCoiObject(entityNum).pipe(
          take(1),
          map(coi => coi ? {
            versionNumber,
            name: coi.insuranceProvider || coi.friendlyName || 'Untitled COI',
            status: coi.status || 'Superseded',
            supersededDate: coi.updatedAt || coi.createdAt || '',
            entityNum: coi.coiNum || entityNum,
            entityType: 'coi'
          } : null),
          catchError(() => of(null))
        );

      default:
        return of(null);
    }
  }

  openVersion(version: VersionRow): void {
    let route = '';
    switch (version.entityType) {
      case 'contract':
        route = `/securehome/contract/${version.entityNum}`;
        break;
      case 'baa':
        route = `/securehome/baa/${version.entityNum}`;
        break;
      case 'coi':
        route = `/securehome/coi/${version.entityNum}`;
        break;
    }
    if (route) {
      window.open(route, '_blank');
    }
  }
}
